<template>
  <q-select
    ref="emailRef"
    hide-bottom-space
    transition-duration="100"
    :placeholder="!emails.length ? 'Enter an email' : null"
    :model-value="emails"
    filled
    use-input
    use-chips
    multiple
    hide-dropdown-icon
    input-debounce="0"
    new-value-mode="add-unique"
    no-error-icon
    lazy-rules
    :rules="[(val) => val.length === 0 || isValidEmail(val)]"
    @add="newEmail"
    @remove="deleteEmail"
    @blur="onBlur"
    @input-value="inputValueChange"
  >
    <template #selected-item="scope">
      <q-chip
        removable
        dense
        :tabindex="scope.tabindex"
        :color="getChipColor(scope.opt)"
        @remove="scope.removeAtIndex(scope.index)"
      >
        {{ scope.opt }}
      </q-chip>
    </template>
  </q-select>
</template>

<script lang="ts">
import { QSelect } from 'quasar'
import { isValidEmail as isValidEmailCommon } from 'common'
import { isInternalEmail } from '@/utils'
import { useUserStore } from '@/stores/user'

export const isValidEmail = (val: string | string[]): true | 'Invalid email' => {
  const fnc = (v: string) => {
    return isValidEmailCommon(v) || 'Invalid email'
  }
  return typeof val === 'string' ? fnc(val) : true
}

const deduplicateList = <T,>(list: T[]) => {
  return Array.from(new Set(list))
}

export default {
  name: 'EmailSelect',
  props: {
    emails: {
      type: Array<string>,
      required: true
    },
    canExternallyShare: {
      type: Boolean,
      default: null
    }
  },
  emits: ['update-emails'],
  data() {
    return {
      isValidEmail,
      currentEditValue: ''
    }
  },
  computed: {
    organizationMembers() {
      return useUserStore().organizationMembers
    }
  },
  methods: {
    newEmail(details: { index: number; value: string }) {
      const emailRef = this.$refs['emailRef'] as QSelect
      const value = details.value.split(',').join(' ').split(';').join(' ')
      if (
        value
          .split(' ')
          .map(emailRef.validate)
          .filter((_) => !_).length === 0
      ) {
        const emails = new Set(
          value
            .split(' ')
            .map((_) => _.trim().toLowerCase())
            .filter((_) => _)
        )
        const newList = this.emails.concat(...emails)
        const deduplicatedEmails = deduplicateList(newList)
        if (deduplicatedEmails.length !== newList.length) {
          this.$q.notify({
            message: 'Removed duplicate emails',
            color: 'positive',
            position: 'top'
          })
        }
        this.$emit('update-emails', deduplicatedEmails)
        emailRef.resetValidation()
      }
    },
    deleteEmail(details: { index: number; value: string }) {
      this.$emit(
        'update-emails',
        this.emails.filter((_, idx) => idx !== details.index)
      )
      const emailRef = this.$refs['emailRef'] as QSelect
      emailRef.resetValidation()
    },
    onBlur() {
      const emailRef = this.$refs['emailRef'] as QSelect
      if (this.currentEditValue) {
        this.newEmail({ index: this.emails.length, value: this.currentEditValue })
        this.currentEditValue = ''
      } else {
        emailRef.resetValidation()
      }
    },
    inputValueChange(value: string) {
      this.currentEditValue = value
    },
    getChipColor(email: string) {
      if (this.canExternallyShare !== null) {
        if (!this.emailCanBeExternallyShared(email)) {
          if (this.$q.dark.isActive) {
            return 'primary-light'
          }
          return 'primary-extra-light'
        }
      }
      if (this.$q.dark.isActive) {
        return 'grey-7'
      }
      return 'grey-3'
    },
    emailCanBeExternallyShared(email: string) {
      if (this.canExternallyShare) {
        return true
      }

      return isInternalEmail(email, this.organizationMembers)
    }
  }
}
</script>
